import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, CheckCheck, X, Image, ChefHat, FileSpreadsheet, Zap, Sparkles, ExternalLink, Calculator, Cake, AlertTriangle, Calendar, PackageX, TrendingDown, FileDown } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { usePlanFeatures } from "../hooks/usePlanFeatures";

// Each notification type → which sidebar module it belongs to. Used to hide
// notifications about modules the user can't access (e.g. Starter doesn't see
// inventory/finance alerts).
const TYPE_MODULE = {
  image_generated:    "menus",
  images_batch_done:  "menus",
  recipe_generated:   "recipes",
  recipes_batch_done: "recipes",
  bulk_import_complete: "tpv",
  dishes_created:     "menus",
  inventory_reconcile: "inventory",
  inventory_enrichment: "inventory",
  weekly_pnl:         "finance",
  invoice_due_soon:   "finance",
  finance_insight:    "finance",
  finance_export:     "finance",
  cash_flow_alert:    "finance",
  variance_alert:     "inventory",
  low_stock:          "inventory",
  order_stale:        "shopping_lists",
  order_supplier_reply: "shopping_lists",
  module_unlock_suggestion: null,
  birthday_digest:    "customers",
};

const TYPE_CONFIG = {
  image_generated:    { icon: Image,           color: "text-violet-500",  bg: "bg-violet-50 dark:bg-violet-950/30", route: "/app/recipes" },
  images_batch_done:  { icon: Sparkles,        color: "text-violet-500",  bg: "bg-violet-50 dark:bg-violet-950/30", route: "/app/recipes" },
  recipe_generated:   { icon: ChefHat,         color: "text-amber-500",   bg: "bg-amber-50 dark:bg-amber-950/30",  route: "/app/recipes" },
  recipes_batch_done: { icon: ChefHat,         color: "text-amber-500",   bg: "bg-amber-50 dark:bg-amber-950/30",  route: "/app/recipes" },
  bulk_import_complete:{ icon: FileSpreadsheet, color: "text-blue-500",    bg: "bg-blue-50 dark:bg-blue-950/30",    route: "/app/tpv" },
  dishes_created:     { icon: Zap,             color: "text-green-500",   bg: "bg-green-50 dark:bg-green-950/30",   route: "/app/menus" },
  inventory_reconcile:{ icon: FileSpreadsheet, color: "text-teal-500",    bg: "bg-teal-50 dark:bg-teal-950/30",    route: "/app/inventory" },
  inventory_enrichment:{ icon: Sparkles,       color: "text-teal-500",    bg: "bg-teal-50 dark:bg-teal-950/30",    route: "/app/inventory" },
  // Contabilidad / finance channels
  weekly_pnl:         { icon: Calculator,      color: "text-indigo-500",  bg: "bg-indigo-50 dark:bg-indigo-950/30", route: "/app/finance" },
  invoice_due_soon:   { icon: Calendar,        color: "text-red-500",     bg: "bg-red-50 dark:bg-red-950/30",       route: "/app/finance?tab=inbox" },
  finance_insight:    { icon: Sparkles,        color: "text-violet-500",  bg: "bg-violet-50 dark:bg-violet-950/30", route: "/app/finance" },
  finance_export:     { icon: FileDown,        color: "text-muted-foreground",   bg: "bg-muted/50 dark:bg-slate-950/30",   route: "/app/finance" },
  cash_flow_alert:    { icon: TrendingDown,    color: "text-red-700",     bg: "bg-red-50 dark:bg-red-950/30",       route: "/app/finance?tab=forecast" },
  // Inventario
  variance_alert:     { icon: AlertTriangle,   color: "text-amber-700",   bg: "bg-amber-50 dark:bg-amber-950/30",  route: "/app/inventory" },
  low_stock:          { icon: PackageX,        color: "text-red-500",     bg: "bg-red-50 dark:bg-red-950/30",       route: "/app/inventory" },
  order_stale:        { icon: PackageX,        color: "text-orange-700",  bg: "bg-orange-50 dark:bg-orange-950/30", route: "/app/receptions" },
  order_supplier_reply: { icon: PackageX,      color: "text-emerald-700", bg: "bg-emerald-50 dark:bg-emerald-950/30", route: "/app/receptions" },
  module_unlock_suggestion: { icon: Sparkles,  color: "text-violet-600",  bg: "bg-violet-50 dark:bg-violet-950/30",  route: "/app/settings?tab=general" },
  // CRM
  birthday_digest:    { icon: Cake,            color: "text-pink-500",    bg: "bg-pink-50 dark:bg-pink-950/30",     route: "/app/clients" },
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "Ahora mismo";
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

// ─── Extracted: single notification item ──────────────────────────────────────
function NotificationItem({ n, onMarkRead, onDelete, onClick }) {
  const cfg = TYPE_CONFIG[n.type] || { icon: Bell, color: "text-muted-foreground", bg: "bg-muted/30" };
  const Icon = cfg.icon;
  const clickable = !!cfg.route;
  return (
    <li
      onClick={() => onClick(n)}
      className={`group flex items-start gap-3 px-4 py-3 border-b border-border/30 last:border-0 transition-colors ${
        n.is_read ? "opacity-60" : "bg-primary/[0.02]"
      } hover:bg-muted/30 ${clickable ? "cursor-pointer" : ""}`}
      data-testid={`notification-item-${n.notification_id}`}
    >
      <div className={`w-8 h-8 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon className={`w-4 h-4 ${cfg.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug">{n.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{n.message}</p>
        <p className="text-[10px] text-muted-foreground/70 mt-1 flex items-center gap-1">
          {formatTime(n.created_at)}
          {clickable && <ExternalLink className="w-2.5 h-2.5 inline opacity-50" />}
        </p>
      </div>
      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        {!n.is_read && (
          <button onClick={(e) => onMarkRead(n.notification_id, e)}
            className="w-6 h-6 rounded-lg hover:bg-primary/10 flex items-center justify-center" title="Marcar como leida"
            data-testid={`mark-read-${n.notification_id}`}>
            <Check className="w-3.5 h-3.5 text-primary" />
          </button>
        )}
        <button onClick={(e) => onDelete(n.notification_id, e)}
          className="w-6 h-6 rounded-lg hover:bg-destructive/10 flex items-center justify-center" title="Eliminar"
          data-testid={`delete-notif-${n.notification_id}`}>
          <X className="w-3.5 h-3.5 text-destructive" />
        </button>
      </div>
    </li>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const pollRef = useRef(null);
  const { hasModule } = usePlanFeatures();

  // Filtered list — drop notifications whose module is not unlocked for this plan.
  // Untyped notifications (no entry in TYPE_MODULE) always pass through.
  const visibleNotifications = useMemo(() => {
    return notifications.filter(n => {
      const mod = TYPE_MODULE[n.type];
      return !mod || hasModule(mod);
    });
  }, [notifications, hasModule]);
  const visibleUnread = useMemo(
    () => visibleNotifications.filter(n => !n.is_read).length,
    [visibleNotifications]
  );

  const prevUnreadRef = useRef(0);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await api.getNotifications();
      const newNotifs = data.notifications || [];
      const newUnread = data.unread || 0;

      // Show toast for new batch-done notifications the user hasn't seen yet —
      // skip the ones whose module is locked (Starter shouldn't get menu/recipe
      // batch toasts if Starter has those modules; this is module-aware).
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        const batchTypes = ["images_batch_done", "recipes_batch_done"];
        const fresh = newNotifs.filter(
          n => !n.is_read && batchTypes.includes(n.type) && !notifications.find(old => old.notification_id === n.notification_id)
        );
        for (const n of fresh) {
          const mod = TYPE_MODULE[n.type];
          if (mod && !hasModule(mod)) continue;
          toast.success(n.title, { description: n.message, duration: 6000 });
        }
      }

      prevUnreadRef.current = newUnread;
      setNotifications(newNotifs);
      setUnread(newUnread);
    } catch {/* silently ignore */}
  }, [notifications, hasModule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load + polling every 15s (lightweight — no batch progress polling elsewhere)
  useEffect(() => {
    loadNotifications();
    pollRef.current = setInterval(loadNotifications, 15000);
    return () => clearInterval(pollRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleMarkRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev => prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { toast.error("Error al marcar como leída"); }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      setUnread(prev => {
        const deleted = notifications.find(n => n.notification_id === notificationId);
        return deleted && !deleted.is_read ? Math.max(0, prev - 1) : prev;
      });
    } catch { toast.error("Error al eliminar"); }
  };

  const handleDeleteAll = async () => {
    try {
      await api.deleteAllNotifications();
      setNotifications([]);
      setUnread(0);
    } catch { toast.error("Error al limpiar"); }
  };

  const handleClickNotification = async (n) => {
    const cfg = TYPE_CONFIG[n.type];
    if (!n.is_read) {
      try {
        await api.markNotificationRead(n.notification_id);
        setNotifications(prev => prev.map(x => x.notification_id === n.notification_id ? { ...x, is_read: true } : x));
        setUnread(prev => Math.max(0, prev - 1));
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (cfg?.route) {
      navigate(cfg.route);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="relative w-9 h-9 rounded-xl flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5" />
        {visibleUnread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-700 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none"
            data-testid="notification-badge"
          >
            {visibleUnread > 99 ? "99+" : visibleUnread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 max-h-[480px] bg-background border border-border/60 rounded-2xl shadow-xl z-50 flex flex-col overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <span className="font-semibold text-sm">Notificaciones</span>
            <div className="flex items-center gap-1">
              {visibleUnread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-muted-foreground hover:text-primary px-2 py-1 rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Marcar todas
                </button>
              )}
              {visibleNotifications.length > 0 && (
                <button
                  onClick={handleDeleteAll}
                  className="text-xs text-muted-foreground hover:text-destructive px-2 py-1 rounded-lg hover:bg-destructive/10 transition-colors"
                  data-testid="delete-all-notifications-btn"
                  title="Limpiar todas"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {visibleNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                <Bell className="w-8 h-8 mb-2 opacity-30" />
                <p className="text-sm">Sin notificaciones</p>
              </div>
            ) : (
              <ul>
                {visibleNotifications.map(n => (
                  <NotificationItem
                    key={n.notification_id}
                    n={n}
                    onMarkRead={handleMarkRead}
                    onDelete={handleDelete}
                    onClick={handleClickNotification}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
